/**
 * Runtime i18next singleton for the ads client.
 *
 * Bundled resources produced by `generateI18nResources.js` are imported
 * eagerly so that every translation lookup is in-memory at startup.
 * `initI18n` is called once from `App.tsx` with the Contentful locale
 * resolved by `GlobalContext`; it sets the active i18next language so
 * `useTranslation()` returns the right strings on first render.
 *
 * Configuration matches press-site's next-i18next config where comparable:
 *   - English fallback if a key is missing in the active language.
 *   - Single-brace interpolation (`{name}`) with `escapeValue: false` so
 *     React can render inline elements safely.
 *   - `returnEmptyString: false` so a missing/empty value falls through
 *     to the per-call fallback label supplied to `t()`.
 */
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import resources from "../../resources.json";
import { Namespaces, getObelixFriendlyLocale } from "./ObelixProvider";

export const defaultNS = Namespaces.common;
export const namespaces = [Namespaces.common, Namespaces.nav, Namespaces.footer];

export const i18n = i18next.createInstance();

let initialized = false;

/**
 * Initializes the i18next singleton (idempotent). After the first call the
 * instance is ready and `t()` can be used from any component.
 *
 * `locale` is the Contentful `<lang>-<region>` code from `GlobalContext`.
 * We translate that into the ALH-friendly locale used as the i18next
 * language key; unknown regions default to the language portion only.
 */
export const initI18n = async (locale?: string): Promise<void> => {
  if (!initialized) {
    await i18n.use(initReactI18next).init({
      resources,
      lng: getObelixFriendlyLocale(locale ?? "en-US"),
      fallbackLng: "en",
      defaultNS,
      ns: namespaces,
      interpolation: {
        escapeValue: false,
        prefix: "{",
        suffix: "}",
      },
      returnEmptyString: false,
      react: {
        useSuspense: false,
      },
      debug: false,
    });
    initialized = true;
    return;
  }

  // After the first mount, follow the resolved locale if it changes.
  if (locale) {
    await i18n.changeLanguage(getObelixFriendlyLocale(locale));
  }
};
